import { type VoidAsyncResult, combineVoidAsyncResults } from "@/cache/AsyncResult";
import { CacheDatumState } from "@/cache/CacheDatumState";
import { networkCaches } from "@/cache/GlobalCache";
import { buildNwaTypeFilterExpression } from "@/constants/newHazards";
import { emptyFeatureCollection } from "@/geojson";
import { type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import type { LayerKindName } from "@/layers/utility/createLayerObject";
import { expireAllTiles } from "@/layers/utility/expireAllTiles";
import { renderPopup } from "@/layers/utility/popup";
import { roundToNearestCacheableTime } from "@/time/roundByLayerKind";
import { safeFmt } from "@/utility/safeFmt";
import { VectorTileData } from "@mm/api.meteomatics.com";
import type { GuiTimeZone, NwaLayer } from "@mm/metx-workbench.meteomatics.com";
import { flatten } from "lodash";
import type { DateTime } from "luxon";
import type { GeoJSONSourceSpecification, MapMouseEvent } from "mapbox-gl";
import mapboxgl from "mapbox-gl";
import { ChangeDetector } from "../ChangeDetector";
import type { ScenePublicApi } from "../Compositor";

/**
 * Minimal vector-tile client for National Weather Alerts (NWA).
 * - Loads /mvt/weather_reports/nwa:0/style.json
 * - Builds Mapbox sources/layers
 * - Applies client-side filter from custom_options.alert_types_filter (optional)
 */
export class NwaLayerImpl extends LayerBase<NwaLayer> {
  private readonly registeredSourceIds: string[] = [];
  private readonly registeredLayerIds: string[] = [];
  private styleLoaded = false;
  private displayDateTime: ChangeDetector<string> | undefined;
  private popup = new mapboxgl.Popup({ maxWidth: "420px", className: "mapbox-popover mapbox-popover-nwa" });
  private listenersAttached = false;
  private clickHandler?: (e: MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => void;
  private mouseEnterHandler?: (e: MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] }) => void;
  private mouseLeaveHandler?: () => void;

  constructor(uid: number, props: NwaLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(uid, props, scene, timezone);

    // Placeholder to stabilize initial z-order (async style load/layer add)
    this.addPlaceholderLayer();

    this.displayDateTime = new ChangeDetector(
      roundToNearestCacheableTime(this.scene.getDisplayTimeWithOffset(), this.props.kind as LayerKindName).toISO(),
    );

    // Load vector layer style via cache (same pattern as IsoLines/Aviation)
    const { asynchronous } = networkCaches.spatiotemporal_vector_tile_cache.retrieveVectorLayerStyle({
      vectorTileData: VectorTileData.NWA,
      // NWA style does not need a weather parameter; we pass a placeholder to satisfy typing
      parameter: "nwa:0" as any,
    });
    asynchronous
      .then((style) => {
        if (style) {
          this.createSourcesAndLayers(style);
          // Apply initial state
          this.toggleVisibility(this.props.show);
          this.applyOpacity(this.props.opacity);
          this.applyAlertTypeFilter(this.props.custom_options?.alert_types_filter ?? null);
          this.styleLoaded = true;
        }
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error("Failed to load NWA style from cache", e);
      });
  }

  // ----- LayerBase API -----

  checker(): PropsChecker<NwaLayer, LayerBase<NwaLayer>> {
    type CheckerProps = CheckedProps<NwaLayer>;
    const checkLayerProp = (property: CheckerProps) => (prev: any, curr: any) => {
      const changed = prev[property] !== curr[property];
      if (!changed) {
        return false;
      }

      this.setLayerProps({ [property]: curr[property] });

      switch (property) {
        case "show":
          this.toggleVisibility(curr.show);
          break;
        case "opacity":
          this.applyOpacity(curr.opacity);
          break;
        case "custom_options":
          this.applyAlertTypeFilter(curr.custom_options?.alert_types_filter ?? null);
          break;
        case "text_size":
          break;
        case "calibrated":
          break;
        default: {
          const _exhaustive: never = property;
          return _exhaustive;
        }
      }
      return true;
    };

    return {
      show: checkLayerProp("show"),
      opacity: checkLayerProp("opacity"),
      custom_options: checkLayerProp("custom_options"),
      calibrated: checkLayerProp("calibrated"),
      text_size: checkLayerProp("text_size"),
    };
  }

  isRebuffering(_time: DateTime): boolean {
    const dt = roundToNearestCacheableTime(this.scene.getDateTimeWithOffset(_time), this.props.kind as LayerKindName);
    return !this.hasAllSourcesLoaded(dt);
  }

  beforeRender(): void {
    if (!this.registeredSourceIds.length || !this.styleLoaded || !this.displayDateTime) {
      return;
    }
    const currentIso = this.scene.getDisplayTimeWithOffset().toISO();
    if (this.displayDateTime.changed(currentIso)) {
      this.popup.remove();
      expireAllTiles(this.scene.getMapboxMap(), this.registeredSourceIds);
    }
  }

  fetchData(_timeFrame: DateTime): VoidAsyncResult {
    const dt = roundToNearestCacheableTime(
      this.scene.getDateTimeWithOffset(_timeFrame),
      this.props.kind as LayerKindName,
    );
    return combineVoidAsyncResults(this.getTileRequestList(dt));
  }

  triggerRefetchData(): void {
    // Not needed for NWA; left as no-op
  }

  moveZIndex(beforeLayerId?: string): void {
    const map = this.scene.getMapboxMap();
    if (map.getLayer(this.placeholderId)) {
      map.moveLayer(this.placeholderId, beforeLayerId);
    }
    // Move all registered layers maintaining order
    for (const id of this.registeredLayerIds) {
      if (map.getLayer(id)) {
        map.moveLayer(id, beforeLayerId);
      }
    }
  }

  removeLayer(): void {
    const map = this.scene.getMapboxMap();
    const layerIds = this.registeredLayerIds.slice();

    if (this.clickHandler) {
      map.off("click", layerIds, this.clickHandler);
    }

    if (this.mouseEnterHandler) {
      map.off("mouseenter", layerIds, this.mouseEnterHandler);
    }

    if (this.mouseLeaveHandler) {
      map.off("mouseleave", layerIds, this.mouseLeaveHandler);
    }

    this.listenersAttached = false;

    for (const id of this.registeredLayerIds) {
      if (map.getLayer(id)) {
        map.removeLayer(id);
      }
    }
    this.registeredLayerIds.length = 0;

    for (const id of this.registeredSourceIds) {
      if (map.getSource(id)) {
        map.removeSource(id);
      }
    }
    this.registeredSourceIds.length = 0;

    if (map.getLayer(this.placeholderId)) {
      map.removeLayer(this.placeholderId);
    }
    if (map.getSource(this.placeholderId)) {
      map.removeSource(this.placeholderId);
    }
  }

  humanReadableId(): string {
    return safeFmt`metx.nwa#${this.uid}`;
  }

  // ----- Internals -----

  private get placeholderId(): string {
    return `metx.nwa#placeholder#${this.uid}`;
  }

  private addPlaceholderLayer() {
    const map = this.scene.getMapboxMap();
    if (map.getLayer(this.placeholderId)) {
      return;
    }

    const source: GeoJSONSourceSpecification = { type: "geojson", data: emptyFeatureCollection };
    map.addSource(this.placeholderId, source);

    map.addLayer({
      id: this.placeholderId,
      type: "symbol",
      source: this.placeholderId,
    });
  }

  private createSourcesAndLayers(style: { sources: Record<string, any>; layers: any[] }) {
    const map = this.scene.getMapboxMap();

    // Add sources with UID namespacing and attach fetchTile that goes through the vector tile cache
    for (const [sourceId, sourceDef] of Object.entries(style.sources)) {
      const namespaced = this.nsSourceId(sourceId);
      if (!map.getSource(namespaced)) {
        // @ts-ignore extend with fetchTile
        map.addSource(namespaced, {
          ...sourceDef,
          fetchTile: (params: { x: number; y: number; z: number }) => {
            const dateTime = roundToNearestCacheableTime(
              this.scene.getDisplayTimeWithOffset(),
              this.props.kind as LayerKindName,
            );
            const { asynchronous } = networkCaches.spatiotemporal_vector_tile_cache.retrieveVectorTile({
              boundingBoxLimit: null,
              model: "",
              parameter: "nwa:0",
              source: namespaced,
              vectorTileData: VectorTileData.NWA,
              geometry: params,
              datetime: dateTime,
            });
            return asynchronous;
          },
        });
        this.registeredSourceIds.push(namespaced);
      }
    }

    // Add layers with UID namespacing and insert before placeholder
    for (const layer of style.layers) {
      const namespacedLayerId = this.nsLayerId(layer.id);
      if (!map.getLayer(namespacedLayerId)) {
        const cloned: any = JSON.parse(JSON.stringify(layer));
        if (cloned.source) {
          cloned.source = this.nsSourceId(String(cloned.source));
        }
        cloned.id = namespacedLayerId;
        map.addLayer(cloned, this.placeholderId);
        this.registeredLayerIds.push(namespacedLayerId);
      }
    }
    if (!this.listenersAttached && this.registeredLayerIds.length) {
      this.setEventListeners();
      this.listenersAttached = true;
    }
  }

  private setEventListeners() {
    const map = this.scene.getMapboxMap();
    const layerIds = this.registeredLayerIds.slice();

    // Cursor feedback when hovering NWA features (polygons or symbols)
    this.mouseEnterHandler = (e: MapMouseEvent) => {
      if (!e.features || !e.features.length) {
        return;
      }
      map.getCanvas().style.cursor = "pointer";
    };
    map.on("mouseenter", layerIds, this.mouseEnterHandler);

    this.mouseLeaveHandler = () => {
      map.getCanvas().style.cursor = "";
    };
    map.on("mouseleave", layerIds, this.mouseLeaveHandler);

    // Popup with properties on click (supports Point annotations and polygon fills)
    this.clickHandler = (e: MapMouseEvent) => {
      if (!e.features || !e.features.length) {
        return;
      }
      const feature = e.features[0];

      let lngLat = e.lngLat; // default for polygons/lines
      if (feature.geometry && feature.geometry.type === "Point") {
        const coords = (feature.geometry as any).coordinates.slice();
        while (Math.abs(e.lngLat.lng - coords[0]) > 180) {
          coords[0] += e.lngLat.lng > coords[0] ? 360 : -360;
        }
        lngLat = new mapboxgl.LngLat(coords[0], coords[1]);
      }

      this.popup.setLngLat(lngLat).setHTML(this.buildNwaPopupHTML(feature)).addTo(map);
    };
    map.on("click", layerIds, this.clickHandler);
  }

  private buildNwaPopupHTML(feature: mapboxgl.MapboxGeoJSONFeature): string {
    const p: any = feature.properties || {};

    // Field normalization based on docs
    const originator_name = p.originator_name ?? p.issuer ?? p.source ?? "";
    const headline = p.headline ?? p.title ?? "";
    const event = p.event ?? "";
    const description = p.description ?? p.desc ?? "";
    const instruction = p.instruction ?? "";
    const location = p.location ?? "";
    const region_code = p.region_code ?? "";
    const begins = p.begins ?? p.start ?? p.valid_from ?? "";
    const ends = p.ends ?? p.end ?? p.valid_to ?? "";
    const level = p.level ?? p.severity ?? "";
    const type = p.type ?? p.hazard_code ?? "";
    const cancelled = p.cancelled ?? "";

    const row = (k: string, v: unknown) =>
      v == null || String(v) === "" ? null : ([k, String(v)] as [string, unknown]);

    const rows = [
      row("Originator", originator_name),
      row("Headline", headline),
      row("Event", event),
      row("Description", description),
      row("Instruction", instruction),
      row("Location", location),
      row("Region Code", region_code),
      row("Begins", begins),
      row("Ends", ends),
      row("Level", level),
      row("Type", type),
      row("Cancelled", cancelled),
    ].filter(Boolean) as Array<[string, unknown]>;

    return renderPopup({
      title: `${type || "Weather Alert"}${level ? ` – ${level}` : ""}`,
      severity: level || null,
      rows,
    });
  }

  private applyOpacity(opacity: number) {
    const map = this.scene.getMapboxMap();
    for (const id of this.registeredLayerIds) {
      const layer = map.getLayer(id);
      if (!layer) {
        continue;
      }

      // Try common paint properties; ignore if not present
      // (NWA style usually contains fill/line/label layers)
      try {
        map.setPaintProperty(id, "fill-opacity", opacity);
      } catch {}
      try {
        map.setPaintProperty(id, "line-opacity", opacity);
      } catch {}
      try {
        map.setPaintProperty(id, "text-opacity", opacity);
      } catch {}
      try {
        const name = "symbol-opacity" as keyof mapboxgl.PaintSpecification;
        const value = opacity as mapboxgl.PaintSpecification[typeof name];
        map.setPaintProperty(id, name, value);
      } catch {}
    }
  }

  private toggleVisibility(show: boolean) {
    const map = this.scene.getMapboxMap();
    const visibility = show ? "visible" : "none";
    for (const id of this.registeredLayerIds) {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", visibility);
      }
    }
  }

  /**
   * Apply client-side type filter if provided.
   * We support both `type` and `hazard_code` property names (whichever exists in the style),
   * by wrapping in an `any(...)` expression.
   */
  private applyAlertTypeFilter(codes: string[] | null) {
    const map = this.scene.getMapboxMap();
    const normalized = !codes || codes.length === 0 ? null : codes;
    const filter = buildNwaTypeFilterExpression(normalized);

    for (const id of this.registeredLayerIds) {
      if (!filter) {
        // Clear any previous filter completely
        map.setFilter(id, null);
      } else {
        // Replace filter atomically (do NOT combine with existing filter)
        map.setFilter(id, filter as any);
      }
    }
  }

  private nsSourceId(id: string) {
    return `${this.mapboxIndex}#src#${id}`;
  }

  private nsLayerId(id: string) {
    return `${this.mapboxIndex}#lyr#${id}`;
  }

  get mapboxIndex(): string {
    return this.placeholderId;
  }

  getActiveWeatherParametersAsString() {
    return [];
  }

  getActiveWeatherParameters() {
    return [];
  }

  private getTileRequestList(dateTime: DateTime) {
    const map = this.scene.getMapboxMap();
    const statuses = flatten(
      this.registeredSourceIds.map((sourceName) => {
        const source: any = map.getSource(sourceName);
        if (source && typeof source.coveringTiles === "function") {
          return source.coveringTiles().map((tile: any) =>
            networkCaches.spatiotemporal_vector_tile_cache.retrieveVectorTile({
              boundingBoxLimit: null,
              model: "",
              parameter: "nwa:0",
              source: sourceName,
              vectorTileData: VectorTileData.NWA,
              geometry: { x: tile.x, y: tile.y, z: tile.z },
              datetime: dateTime,
            }),
          );
        }
        return [] as any[];
      }),
    );
    return statuses;
  }

  private hasAllSourcesLoaded(dateTime: DateTime) {
    const tileStates = this.getTileRequestList(dateTime).map(({ synchronous }: any) => synchronous);
    return !tileStates.includes(CacheDatumState.Pending);
  }
}
